import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ArrowRight, Loader2 } from 'lucide-react'
import { trpc } from '@/lib/trpc/client'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

const quickStarts = [
  {
    category: 'Residential Buildings',
    image: '/images/residential.jpg',
    examples: [
      { label: '8-floor apartment building', prompt: 'Residential building 8 floors, 120 apartments: complete system foundation, waterproofing, flooring, sealing' },
      { label: 'Single family house 180m²', prompt: 'Single-family house 180sqm: foundation, roof, bathrooms, kitchen, terrace' },
      { label: '4-story townhouse complex', prompt: '4-story townhouse complex, 24 units: foundation waterproofing, facade protection, balcony systems, interior flooring' },
    ]
  },
  {
    category: 'Commercial Buildings',
    image: '/images/construction-system.jpg',
    examples: [
      { label: 'Office building 12 floors', prompt: 'Office building 12 floors: foundation waterproofing, facade sealing, parking deck, interior flooring' },
      { label: 'Retail center 5,000m²', prompt: 'Shopping center 5000sqm: structural waterproofing, floor coatings, bathroom facilities, loading dock' },
      { label: 'Warehouse facility', prompt: 'Industrial warehouse 8000sqm: floor coating system, loading bay sealing, roof waterproofing' },
    ]
  },
  {
    category: 'Interior Wet Areas',
    image: '/images/bathroom.jpg',
    examples: [
      { label: '12 commercial bathrooms', prompt: '12 commercial bathrooms: waterproofing, adhesive, tiles, joints, sealing' },
      { label: 'Hotel bathroom renovation', prompt: 'Hotel 45 bathrooms renovation: waterproofing membrane, tile adhesive, grout, joints' },
      { label: 'Spa wet areas 200m²', prompt: 'Spa facility wet areas 200sqm: steam rooms, showers, changing rooms - waterproofing and tiling' },
    ]
  },
  {
    category: 'Pools & Water Features',
    image: '/images/pool.jpg',
    examples: [
      { label: 'Residential pool 40m²', prompt: 'Swimming pool 40sqm, exterior, reinforced concrete, permanent water contact' },
      { label: 'Hotel rooftop pool', prompt: 'Hotel rooftop pool 60sqm with surrounding deck: waterproofing, tiling, expansion joints' },
      { label: 'Public fountain', prompt: 'Public decorative fountain 30sqm: structural repairs, waterproofing, protective coatings' },
    ]
  },
  {
    category: 'Renovations',
    image: '/images/value-chain.jpg',
    examples: [
      { label: 'Historic building restoration', prompt: 'Historic building restoration: facade repair, waterproofing upgrades, interior refinishing, heritage compliance' },
      { label: 'Villa renovation 250m²', prompt: 'Villa renovation 250sqm: basement waterproofing, bathroom upgrades, kitchen remodel, new terrace system' },
      { label: 'Apartment refurbishment', prompt: 'Apartment building refurbishment 80 units: bathroom waterproofing, kitchen updates, balcony repairs' },
    ]
  },
  {
    category: 'Infrastructure',
    image: '/images/winning-projects.jpg',
    examples: [
      { label: 'Parking structure 300 spaces', prompt: 'Underground parking 300 spaces, 3 levels: structural waterproofing, floor coating, joint sealing, drainage' },
      { label: 'Bridge deck renovation', prompt: 'Highway bridge deck 500m: concrete repair, waterproofing membrane, protective coating, expansion joints' },
      { label: 'Tunnel waterproofing', prompt: 'Traffic tunnel 2km: waterproofing system, drainage, fire protection coatings, joint sealing' },
    ]
  }
]

export default function Home() {
  const navigate = useNavigate()
  const [showChat, setShowChat] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const consultationMutation = trpc.consultation.chat.useMutation()

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleInitialSubmit = async (message: string) => {
    setShowChat(true)
    const initialMessages = [{ role: 'user' as const, content: message }]
    setMessages(initialMessages)
    setIsLoading(true)

    try {
      const result = await consultationMutation.mutateAsync({
        messages: initialMessages,
      })

      const assistantMessage = result.content
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: assistantMessage
      }])

      const hasAllInfo = assistantMessage.toLowerCase().includes('preparing the complete specification') ||
                        assistantMessage.toLowerCase().includes('preparing the specification') ||
                        assistantMessage.toLowerCase().includes('preparing your complete') ||
                        assistantMessage.toLowerCase().includes('we\'re preparing') ||
                        assistantMessage.toLowerCase().includes('have all the necessary details') ||
                        assistantMessage.toLowerCase().includes('have all the details') ||
                        assistantMessage.toLowerCase().includes('all the necessary details') ||
                        assistantMessage.toLowerCase().includes('excellent! i have all')

      if (hasAllInfo) {
        const finalMessages = [...initialMessages, { role: 'assistant', content: assistantMessage }]
        setTimeout(() => {
          const messagesParam = encodeURIComponent(JSON.stringify(finalMessages))
          navigate(`/proposal/review?messages=${messagesParam}`)
        }, 3000)
      }
    } catch (error) {
      console.error('Error:', error)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, an error occurred. Please try again.'
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return

    const userMessage: Message = { role: 'user', content: input }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setInput('')
    setIsLoading(true)

    try {
      const result = await consultationMutation.mutateAsync({
        messages: updatedMessages,
      })

      const assistantMessage = result.content
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: assistantMessage
      }])

      const finalMessages = [...updatedMessages, { role: 'assistant', content: assistantMessage }]
      const hasAllInfo = assistantMessage.toLowerCase().includes('preparing the complete specification') ||
                        assistantMessage.toLowerCase().includes('preparing the specification') ||
                        assistantMessage.toLowerCase().includes('preparing your complete') ||
                        assistantMessage.toLowerCase().includes('we\'re preparing') ||
                        assistantMessage.toLowerCase().includes('have all the necessary details') ||
                        assistantMessage.toLowerCase().includes('have all the details') ||
                        assistantMessage.toLowerCase().includes('all the necessary details') ||
                        assistantMessage.toLowerCase().includes('excellent! i have all')

      if (hasAllInfo) {
        setTimeout(() => {
          const messagesParam = encodeURIComponent(JSON.stringify(finalMessages))
          navigate(`/proposal/review?messages=${messagesParam}`)
        }, 3000)
      }
    } catch (error) {
      console.error('Error:', error)
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, an error occurred. Please try again.'
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!showChat) {
      handleInitialSubmit(input)
    } else {
      sendMessage()
    }
  }

  const handleQuickStart = (prompt: string) => {
    setInput(prompt)
    handleInitialSubmit(prompt)
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Sika Header with Tagline */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="/sika-logo.svg"
                alt="Sika"
                width={100}
                height={34}
                className="transition-opacity group-hover:opacity-80"
              />
            </Link>
            <div className="hidden lg:block text-[13px] text-gray-600" style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
              letterSpacing: '-0.006em',
              fontWeight: 400
            }}>
              Engineered Solution for Your Project
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/proposals/" className="text-sm text-gray-700 hover:text-gray-900 transition-colors">
              My Projects
            </Link>
            <a href="/login" className="text-sm text-gray-700 hover:text-gray-900 transition-colors">
              Login
            </a>
            <a href="/signup" className="text-sm bg-black text-white px-6 py-2.5 hover:bg-gray-800 transition-colors">
              Sign up
            </a>
          </nav>
        </div>
      </header>

      {!showChat ? (
        <div className="relative">
          {/* Background Grid Pattern */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }} />

          {/* Compact Input Section */}
          <div className="container mx-auto px-6 max-w-4xl pt-16 pb-16 relative">
            {/* Guidance Panel - Editorial Style */}
            <div className="mb-8 pl-4 border-l-2 border-[#E2001A] relative">
              <div className="max-w-2xl">
                <p className="text-[15px] leading-relaxed text-gray-700" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                  letterSpacing: '-0.011em',
                  fontWeight: 400
                }}>
                  Describe your construction project below. Our AI assistant will analyze your requirements and generate a complete Sika product specification—covering every phase from foundation to finishes.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className={`relative bg-white border-2 transition-all duration-300 shadow-lg ${
                isFocused
                  ? 'border-[#E2001A] shadow-[0_8px_30px_rgb(226,0,26,0.12)]'
                  : 'border-gray-200'
              }`}>
                {/* Corner Brackets - Engineering Style */}
                <div className="absolute -top-2 -left-2 w-6 h-6 border-l-2 border-t-2 border-[#E2001A] opacity-0 transition-opacity duration-300"
                     style={{ opacity: isFocused ? 1 : 0 }} />
                <div className="absolute -top-2 -right-2 w-6 h-6 border-r-2 border-t-2 border-[#E2001A] opacity-0 transition-opacity duration-300"
                     style={{ opacity: isFocused ? 1 : 0 }} />
                <div className="absolute -bottom-2 -left-2 w-6 h-6 border-l-2 border-b-2 border-[#E2001A] opacity-0 transition-opacity duration-300"
                     style={{ opacity: isFocused ? 1 : 0 }} />
                <div className="absolute -bottom-2 -right-2 w-6 h-6 border-r-2 border-b-2 border-[#E2001A] opacity-0 transition-opacity duration-300"
                     style={{ opacity: isFocused ? 1 : 0 }} />

                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  placeholder="Describe your project (e.g., 8-floor residential building, 120 apartments, foundation waterproofing, flooring systems...)"
                  className="w-full px-6 py-5 text-base resize-none border-0 focus:ring-0 bg-transparent placeholder:text-gray-400 outline-none min-h-[120px] text-gray-900"
                  style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSubmit(e)
                    }
                  }}
                />

                <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex items-center justify-between">
                  <span className="text-xs text-gray-500 hidden sm:block" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                    Press <kbd className="px-2 py-1 bg-white border border-gray-300 text-xs font-mono">Enter</kbd> to generate
                  </span>
                  <Button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="bg-gradient-to-b from-[#E2001A] to-[#C4001A] hover:from-[#C4001A] hover:to-[#A30015] text-white px-8 py-3 text-sm font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <span>Generate</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Quick Start Examples */}
          <div className="container mx-auto px-6 max-w-7xl pb-24 relative">
            <h2 className="text-3xl font-bold text-gray-900 mb-10 tracking-tight" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', letterSpacing: '0.02em' }}>
              Choose your project type
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickStarts.map((category, catIdx) => (
                <div
                  key={catIdx}
                  className="space-y-3 group opacity-0 animate-slide-up"
                  style={{ animationDelay: `${catIdx * 100}ms`, animationFillMode: 'forwards' }}
                >
                  {/* Category Image - Bigger */}
                  <div className="relative h-48 overflow-hidden shadow-lg">
                    <img
                      src={category.image}
                      alt={category.category}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white font-bold text-xl tracking-wide" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', letterSpacing: '0.05em' }}>
                        {category.category}
                      </h3>
                    </div>
                  </div>

                  {/* Examples for this category */}
                  <div className="space-y-2">
                    {category.examples.map((example, exIdx) => (
                      <button
                        key={exIdx}
                        onClick={() => handleQuickStart(example.prompt)}
                        className="w-full text-left px-4 py-3 bg-white hover:bg-gray-50 border border-gray-200 hover:border-[#E2001A] transition-all duration-200 group/btn flex items-start justify-between shadow-sm hover:shadow-md"
                      >
                        <div className="flex-1 font-medium text-gray-900 text-sm">
                          {example.label}
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 group-hover/btn:text-[#E2001A] transition-colors flex-shrink-0 mt-0.5" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <main className="container mx-auto px-8 pt-8 pb-32 max-w-6xl">
          <div className="max-w-4xl mx-auto">
            {/* Chat Header Banner - Engineering Style */}
            <div className="mb-6 relative overflow-hidden" style={{
              background: 'linear-gradient(135deg, #E2001A 0%, #C4001A 100%)',
              boxShadow: '0 4px 20px rgba(226, 0, 26, 0.2)'
            }}>
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)'
              }} />

              <div className="relative px-8 py-6 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-2.5 h-2.5 bg-white/80 rounded-full" />
                    <span className="text-white font-bold text-sm tracking-wider" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', letterSpacing: '0.1em' }}>
                      SPECIFICATION ASSISTANT
                    </span>
                  </div>
                  <p className="text-white/90 text-sm" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                    Generating your complete project specification
                  </p>
                </div>

                {/* Corner Accents */}
                <div className="absolute top-2 right-2 w-8 h-8 border-r-2 border-t-2 border-white/30" />
                <div className="absolute bottom-2 left-2 w-8 h-8 border-l-2 border-b-2 border-white/30" />
              </div>
            </div>

            {/* Chat Interface - Clean */}
            <div className="bg-white border border-gray-200 shadow-lg">
              {/* Messages */}
              <div className="max-h-[500px] overflow-y-auto p-6 space-y-4 bg-gray-50">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-[#E2001A] text-white'
                        : 'bg-white border border-gray-200 text-gray-900'
                    }`}>
                      <p className="leading-relaxed whitespace-pre-wrap text-sm">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border border-gray-200 px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#E2001A]" />
                        <span className="text-gray-600 text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="border-t border-gray-200 p-4 bg-white">
                <form onSubmit={handleSubmit} className="flex gap-3">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your response..."
                    className="flex-1 px-4 py-3 bg-white border border-gray-300 focus:border-[#E2001A] text-gray-900 placeholder:text-gray-400 resize-none outline-none text-sm"
                    disabled={isLoading}
                    rows={2}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleSubmit(e)
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    disabled={isLoading || !input.trim()}
                    className="px-6 bg-[#E2001A] hover:bg-[#C4001A] text-white font-semibold disabled:opacity-50 flex items-center gap-2"
                  >
                    {isLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <span>Send</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>

            {/* Manual Continue Button - Backup */}
            {messages.length > 0 && !isLoading && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={() => {
                    const messagesParam = encodeURIComponent(JSON.stringify(messages))
                    navigate(`/proposal/review?messages=${messagesParam}`)
                  }}
                  className="bg-gradient-to-b from-[#E2001A] to-[#C4001A] hover:from-[#C4001A] hover:to-[#A30015] text-white px-8 py-3 text-sm font-semibold transition-all duration-200 flex items-center gap-2 hover:-translate-y-0.5 shadow-md hover:shadow-lg"
                >
                  <span>Continue to Proposal</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </main>
      )}

      {/* Footer - Clean & Simple */}
      {!showChat && (
        <footer className="border-t border-gray-200 py-8 mt-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img
                  src="/sika-logo.svg"
                  alt="Sika"
                  width={80}
                  height={27}
                  className="opacity-60"
                />
                <span className="text-gray-500 text-xs">
                  © {new Date().getFullYear()} Sika AG
                </span>
              </div>

              <div className="text-gray-500 text-xs">
                AI-Powered Specification Generator
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  )
}
